/**
 * Flow-Statistiken Konfiguration
 *
 * Definiert pro Flow welche Metric-Cards, Charts und Detail-Spalten
 * im Statistiken-Tab angezeigt werden.
 */

export interface MetricCardConfig {
  key: string;
  label: string;
  color?: string;
  /** Falls der Wert aus einem verschachtelten Objekt kommt (z.B. ratingVerteilung.5) */
  nestedKey?: string;
}

export interface ChartBarConfig {
  key: string;
  label: string;
  color: string;
}

export interface ChartConfig {
  title: string;
  bars: ChartBarConfig[];
  /** Falls die Daten aus einem verschachtelten Objekt kommen */
  sourceKey?: string;
}

export interface DetailColumnConfig {
  key: string;
  label: string;
  width?: string;
}

export interface ParamsColumnConfig {
  key: string;
  label: string;
}

export interface FlowStatConfig {
  cards: MetricCardConfig[];
  /** Falls gesetzt: diese Cards werden in der Lauf-Tabelle statt cards.slice(0,4) verwendet */
  tableCards?: MetricCardConfig[];
  chart?: ChartConfig;
  detailColumns: DetailColumnConfig[];
  /** Params-Felder die als Extra-Spalten in der RecentRunsTable angezeigt werden */
  paramsColumns?: ParamsColumnConfig[];
  /** String-Felder aus resultSummary die als Extra-Spalten in der RecentRunsTable angezeigt werden */
  summaryStringColumns?: { key: string; label: string }[];
}

const flowStatisticsConfig: Record<string, FlowStatConfig> = {
  'kundenbewertung': {
    cards: [
      { key: 'totalProcessed', label: 'Verarbeitet (gesamt)', color: '#3B82F6' },
      { key: 'rated', label: 'Bewertet', color: '#F59E0B' },
      { key: 'ratingDistribution', label: '5-Sterne (gesamt)', color: '#10B981', nestedKey: 'fiveStar' },
      { key: 'ratingDistribution', label: '1-Stern (gesamt)', color: '#EF4444', nestedKey: 'oneStar' },
      { key: 'ratingDistribution', label: '0-Sterne (gesamt)', color: '#6B7280', nestedKey: 'zeroStar' },
      { key: 'cycleProgress', label: 'Zyklen-Fortschritt', color: '#8B5CF6' },
      { key: 'completedCycles', label: 'Zyklen', color: '#6366F1' },
    ],
    tableCards: [
      { key: 'rated', label: 'Bewertet' },
      { key: 'checked', label: 'Geprüft' },
      { key: 'ratingDistribution', label: '5-Sterne', nestedKey: 'fiveStar' },
      { key: 'completedCycles', label: 'Zyklen' },
    ],
    summaryStringColumns: [{ key: 'idRange', label: 'ID-Range' }],
    chart: {
      title: 'Rating-Verteilung (Läufe im Zeitraum)',
      sourceKey: 'ratingDistribution',
      bars: [
        { key: 'fiveStar', label: '5 Sterne', color: '#10B981' },
        { key: 'zeroStar', label: '0 Sterne', color: '#6B7280' },
        { key: 'oneStar', label: '1 Stern', color: '#EF4444' },
      ],
    },
    detailColumns: [
      { key: 'contactId', label: 'Kontakt-ID', width: '100px' },
      { key: 'contactName', label: 'Name' },
      { key: 'oldRating', label: 'Alt', width: '50px' },
      { key: 'newRating', label: 'Neu', width: '50px' },
      { key: 'orderCount', label: 'Aufträge', width: '80px' },
      { key: 'paymentStatus', label: 'Zahlungsstatus' },
    ],
  },
  'huck-sendungsnummern': {
    cards: [
      { key: 'totalEmails', label: 'E-Mails', color: '#3B82F6' },
      { key: 'validShipments', label: 'Sendungen', color: '#10B981' },
      { key: 'shippingProfilesUpdated', label: 'Profile akt.', color: '#8B5CF6' },
      { key: 'packageNumbersAdded', label: 'Paketnummern', color: '#F59E0B' },
    ],
    chart: {
      title: 'Ergebnis',
      bars: [
        { key: 'packageNumbersAdded', label: 'Hinzugefügt', color: '#10B981' },
        { key: 'packageNumbersSkipped', label: 'Übersprungen', color: '#F59E0B' },
        { key: 'packageNumberErrors', label: 'Fehler', color: '#EF4444' },
      ],
    },
    detailColumns: [
      { key: 'auftragsnummer', label: 'Auftrag', width: '100px' },
      { key: 'paketnummer', label: 'Sendungsnr.' },
      { key: 'spedition', label: 'Spedition', width: '80px' },
      { key: 'versandprofil', label: 'Versandprofil', width: '100px' },
      { key: 'status', label: 'Status', width: '100px' },
    ],
  },
  'shop-markup-pruefung': {
    cards: [
      { key: 'zieleGesamt', label: 'Seiten geplant', color: '#3B82F6' },
      { key: 'gefetcht', label: 'Geprüft', color: '#10B981' },
      { key: 'markupOutOfStock', label: 'OutOfStock-Markup', color: '#F59E0B' },
      { key: 'bestaetigteMismatches', label: 'Mismatches', color: '#EF4444' },
      { key: 'fetchFehler', label: 'Fetch-Fehler', color: '#6B7280' },
    ],
    chart: {
      title: 'Markup-Verteilung',
      bars: [
        { key: 'markupInStock', label: 'InStock', color: '#10B981' },
        { key: 'markupOutOfStock', label: 'OutOfStock', color: '#F59E0B' },
        { key: 'bestaetigteMismatches', label: 'Bestätigte Mismatches', color: '#EF4444' },
        { key: 'fetchFehler', label: 'Fetch-Fehler', color: '#6B7280' },
      ],
    },
    detailColumns: [
      { key: 'url', label: 'Seite' },
      { key: 'variationId', label: 'Variation', width: '90px' },
      { key: 'markup', label: 'Markup', width: '100px' },
      { key: 'status', label: 'Status', width: '110px' },
      { key: 'grund', label: 'Befund' },
    ],
  },
  'pruefung-rechnungskauf': {
    cards: [
      { key: 'totalOrders', label: 'Geprüft', color: '#3B82F6' },
      { key: 'bestellerFreigabe', label: 'Besteller frei', color: '#10B981' },
      { key: 'lieferadresseFreigabe', label: 'Lieferadr. frei', color: '#8B5CF6' },
      { key: 'abgelehnt', label: 'Abgelehnt', color: '#EF4444' },
      { key: 'abgelehntWegen1Stern', label: '1\u2605 abgel.', color: '#EF4444' },
    ],
    chart: {
      title: 'Freigabe-Verteilung',
      bars: [
        { key: 'bestellerFreigabe', label: 'Besteller freigegeben', color: '#10B981' },
        { key: 'lieferadresseFreigabe', label: 'Lieferadresse freigegeben', color: '#8B5CF6' },
        { key: 'abgelehnt', label: 'Abgelehnt', color: '#EF4444' },
        { key: 'abgelehntWegen1Stern', label: '1\u2605 Abgelehnt', color: '#F87171' },
      ],
    },
    detailColumns: [
      { key: 'orderId', label: 'Auftrag', width: '80px' },
      { key: 'firma', label: 'Firma' },
      { key: 'besteller', label: 'Besteller' },
      { key: 'freigabeBesteller', label: 'Besteller', width: '80px' },
      { key: 'freigabeLieferadresse', label: 'Lieferadr.', width: '80px' },
      { key: 'webseite', label: 'Webseite' },
      { key: 'grund', label: 'Grund' },
      { key: 'ablehnungsgrund', label: 'Ablehnungsgrund' },
    ],
  },
  'artikel-sync': {
    cards: [
      { key: 'totalLoaded', label: 'Gesamt', color: '#3B82F6' },
      { key: 'activeCount', label: 'Aktiv', color: '#10B981' },
      { key: 'inactiveCount', label: 'Inaktiv', color: '#EF4444' },
      { key: 'written', label: 'Geschrieben', color: '#8B5CF6' },
    ],
    detailColumns: [],
  },
  'teamshop-artikelanlage': {
    cards: [
      { key: 'totalSelected', label: 'Quellartikel', color: '#3B82F6' },
      { key: 'loaded', label: 'Geladen', color: '#F59E0B' },
      { key: 'mainItemsCreated', label: 'Angelegt', color: '#10B981' },
      { key: 'createErrors', label: 'Fehler', color: '#EF4444' },
    ],
    chart: {
      title: 'Ergebnis',
      bars: [
        { key: 'mainItemsCreated', label: 'Hauptartikel', color: '#10B981' },
        { key: 'childVariantsCreated', label: 'Varianten', color: '#6366F1' },
        { key: 'createErrors', label: 'Fehler', color: '#EF4444' },
        { key: 'childErrors', label: 'Kind-Fehler', color: '#F87171' },
      ],
    },
    paramsColumns: [
      { key: 'name', label: 'Teamshop' },
      { key: 'kategorieId', label: 'Kategorie' },
      { key: 'rabatt', label: 'Rabatt' },
    ],
    detailColumns: [
      { key: 'originalItemId', label: 'Quelle-ID', width: '80px' },
      { key: 'newItemId', label: 'Neu-ID', width: '80px' },
      { key: 'itemName', label: 'Name' },
      { key: 'childCount', label: 'Varianten', width: '80px' },
    ],
  },
  'mahnung-briefversand': {
    cards: [
      { key: 'totalEmails', label: 'E-Mails', color: '#3B82F6' },
      { key: 'totalPdfs', label: 'PDFs', color: '#F59E0B' },
      { key: 'pdfsUploaded', label: 'Hochgeladen', color: '#10B981' },
      { key: 'uploadErrors', label: 'Fehler', color: '#EF4444' },
      { key: 'emailsMoved', label: 'Verschoben', color: '#8B5CF6' },
    ],
    chart: {
      title: 'Upload-Ergebnis',
      bars: [
        { key: 'pdfsUploaded', label: 'Hochgeladen', color: '#10B981' },
        { key: 'uploadErrors', label: 'Fehler', color: '#EF4444' },
        { key: 'emailsMoved', label: 'E-Mails verschoben', color: '#8B5CF6' },
      ],
    },
    detailColumns: [
      { key: 'auftragsnummer', label: 'Auftragsnummer', width: '120px' },
      { key: 'filename', label: 'Dateiname' },
      { key: 'status', label: 'Status', width: '120px' },
      { key: 'emailSubject', label: 'E-Mail Betreff' },
    ],
  },
  'rechnungspruefung-sporttore': {
    cards: [
      { key: 'totalChecked', label: 'Geprüft', color: '#3B82F6' },
      { key: 'matched', label: 'OK', color: '#10B981' },
      { key: 'mismatched', label: 'Abweichend', color: '#F59E0B' },
      { key: 'margeLow', label: 'Marge <30%', color: '#DC2626' },
      { key: 'duplicates', label: 'Duplikate', color: '#EF4444' },
      { key: 'pdfMissing', label: 'PDF fehlt', color: '#6B7280' },
      { key: 'gutschriften', label: 'Gutschriften', color: '#3B82F6' },
      { key: 'errors', label: 'Fehler', color: '#EF4444' },
    ],
    // Detail-Tabelle bewusst leer: Detail-Ansicht ist im Dashboard-Drilldown
    detailColumns: [],
  },
  'rechnungspruefung-glawel': {
    cards: [
      { key: 'totalChecked', label: 'Geprüft', color: '#3B82F6' },
      { key: 'matched', label: 'OK', color: '#10B981' },
      { key: 'mismatched', label: 'Abweichend', color: '#F59E0B' },
      { key: 'margeLow', label: 'Marge <30%', color: '#DC2626' },
      { key: 'duplicates', label: 'Duplikate', color: '#EF4444' },
      { key: 'pdfMissing', label: 'PDF fehlt', color: '#6B7280' },
      { key: 'gutschriften', label: 'Gutschriften', color: '#3B82F6' },
      { key: 'errors', label: 'Fehler', color: '#EF4444' },
    ],
    detailColumns: [
      { key: 'status', label: 'Status', width: '110px' },
      { key: 'belegnr', label: 'Belegnr.' },
      { key: 'auftragsnr', label: 'Auftrag', width: '100px' },
      { key: 'avisBrutto', label: 'AVIS (€)', width: '100px' },
      { key: 'pdfBrutto', label: 'PDF (€)', width: '100px' },
      { key: 'plentyBrutto', label: 'Plenty (€)', width: '100px' },
      { key: 'gutscheinBrutto', label: 'Gutschein (€)', width: '100px' },
      { key: 'margePercent', label: 'Marge %', width: '90px' },
      { key: 'excelPdfDiff', label: 'AVIS-PDF Diff (€)', width: '120px' },
      { key: 'dupTimes', label: 'DUP #', width: '70px' },
      { key: 'hinweis', label: 'Hinweis' },
    ],
  },
  'rechnungspruefung-hela': {
    cards: [
      { key: 'totalChecked', label: 'Geprüft', color: '#3B82F6' },
      { key: 'matched', label: 'Übereinstimmend', color: '#10B981' },
      { key: 'mismatched', label: 'Abweichend', color: '#F59E0B' },
      { key: 'pdfNotFound', label: 'PDF nicht gefunden', color: '#6B7280' },
      { key: 'errors', label: 'Fehler', color: '#EF4444' },
    ],
    detailColumns: [
      { key: 'status', label: 'Status', width: '80px' },
      { key: 'belegnr', label: 'Belegnr.' },
      { key: 'fremdbeleg', label: 'Fremdbeleg' },
      { key: 'excelBrutto', label: 'Excel (€)', width: '100px' },
      { key: 'pdfBrutto', label: 'PDF (€)', width: '100px' },
      { key: 'plentyBrutto', label: 'Plenty (€)', width: '100px' },
      { key: 'difference', label: 'Excel-PDF Diff (€)', width: '110px' },
      { key: 'plentyPdfDeviationPercent', label: 'Plenty-PDF %', width: '100px' },
      { key: 'pdfFile', label: 'PDF-Datei' },
      { key: 'hinweis', label: 'Hinweis' },
    ],
  },
  'vororder-analyse': {
    cards: [
      { key: 'totalArticles', label: 'Artikel gesamt', color: '#3B82F6' },
      { key: 'totalCritical', label: 'Kritisch', color: '#EF4444' },
      { key: 'totalWarning', label: 'Warnung', color: '#F59E0B' },
      { key: 'totalOk', label: 'Versorgt', color: '#10B981' },
      { key: 'totalNotFound', label: 'Nicht gefunden', color: '#6B7280' },
    ],
    detailColumns: [],
  },
  'baelle-fremdlager': {
    cards: [
      { key: 'totalVarianten', label: 'Varianten', color: '#3B82F6' },
      { key: 'aufgestockt', label: 'Aufgestockt', color: '#10B981' },
      { key: 'skipNichtAufMaximalbestand', label: 'Nicht auf Maxbestand', color: '#F59E0B' },
      { key: 'skipKeinMaximalbestand', label: 'Kein Maxbestand', color: '#9CA3AF' },
      { key: 'fehler', label: 'Fehler', color: '#EF4444' },
    ],
    chart: {
      title: 'Ergebnis',
      bars: [
        { key: 'aufgestockt', label: 'Aufgestockt', color: '#10B981' },
        { key: 'skipNichtAufMaximalbestand', label: 'Nicht auf Maxbestand', color: '#F59E0B' },
        { key: 'skipKeinMaximalbestand', label: 'Kein Maxbestand', color: '#9CA3AF' },
        { key: 'fehler', label: 'Fehler', color: '#EF4444' },
      ],
    },
    detailColumns: [
      { key: 'variantNumber', label: 'Variante', width: '100px' },
      { key: 'itemName', label: 'Artikel' },
      { key: 'manufacturer', label: 'Hersteller', width: '100px' },
      { key: 'foreignWarehouse', label: 'Fremdlager', width: '100px' },
      { key: 'anstossPhysical', label: 'Anstoss', width: '70px' },
      { key: 'anstossReserved', label: 'Anstoss Res.', width: '80px' },
      { key: 'foreignReserved', label: 'Fremdl. Res.', width: '80px' },
      { key: 'foreignPhysical', label: 'Fremdbestand', width: '100px' },
      { key: 'target', label: 'Ziel', width: '60px' },
      { key: 'delta', label: 'Delta', width: '60px' },
      { key: 'action', label: 'Aktion', width: '140px' },
    ],
  },
};

/**
 * Gibt die Statistik-Config für einen Flow zurück.
 * Bei unbekannten Flows wird ein generischer Fallback generiert.
 */
export function getFlowStatConfig(flowId: string, aggregated?: Record<string, any>): FlowStatConfig {
  if (flowStatisticsConfig[flowId]) {
    return flowStatisticsConfig[flowId];
  }

  // Fallback: Generische Cards aus aggregierten Daten
  if (!aggregated) {
    return { cards: [], detailColumns: [] };
  }

  const cards: MetricCardConfig[] = Object.entries(aggregated)
    .filter(([, v]) => typeof v === 'number')
    .slice(0, 5)
    .map(([key]) => ({ key, label: key }));

  return { cards, detailColumns: [] };
}

export default flowStatisticsConfig;
